import {
	GetPaymentGateWayResponseData,
	GetPaymentGatewayConstraintsResponse,
	PaymentChannelTab,
} from '@/modules/bank/type'
import { ColumnDef } from '@tanstack/react-table'
import DeletePaymentGateway from './CellAction/components/DeletePaymentGateway'
import { Button } from '@/common/ui/button'
import { Pencil, ExternalLink, Wallet } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Badge } from '@/common/ui/badge'
import { Icons } from '@/common/ui/icons'
import { cn } from '@/common/lib/utils'

import TogglePaymentGatewayStatus from './components/TogglePaymentGatewayStatus'
import TogglePaymentGatewayWithdrawalAccount from './components/TogglePaymentGatewayWithdrawalAccount'
import CreateDepositDialog from './components/CreateDepositDialog'
import CreateWithdrawalDialog from './components/CreateWithdrawalDialog'

const providerNameToUrlMap: Record<string, string> = {
	GPAY: 'https://zapman.net',
	WELLPAY: 'https://bo.paygt-plus.com/login',
	BADOOPAY: 'https://bo.badoopay.com/login',
	'2STEPX': 'https://bo.2stepx.com/login',
	TLPAY: 'https://bo.tlconnectpay.com/login',
	PAYONEX: 'https://admin.payonex.asia/',
	SMILEPAYZ: 'https://merchant.smilepayz.com/',
	ONPAY: 'https://app.onpay.ink/profile',
	BIBBY: 'https://ag.bibbyx.com/',
	SANDSPAY: 'https://bo.sandspay.co/',
	GOALPAY: 'https://client.cluster01.goalpay.vip/',
}

// Helper to get provider min deposit from constraints
const getProviderMinDeposit = (
	provider: string,
	constraints?: GetPaymentGatewayConstraintsResponse,
): number | undefined => {
	if (!constraints) return undefined
	const providerMin = (
		constraints.providerMinDeposits as Record<string, number>
	)?.[provider]
	return providerMin ?? constraints.defaultMinDeposit
}

const renderMaintenanceBadges = (row: GetPaymentGateWayResponseData) => {
	const r = row as GetPaymentGateWayResponseData & {
		maintenanceDepositEndAt?: string | null
		maintenanceWithdrawEndAt?: string | null
		maintenanceDepositStartAt?: string | null
		maintenanceWithdrawStartAt?: string | null
	}

	const deposit = r.maintenanceDepositEndAt || r.maintenanceDepositStartAt
	const withdraw = r.maintenanceWithdrawEndAt || r.maintenanceWithdrawStartAt

	if (!deposit && !withdraw) {
		return (
			<Badge
				variant="secondary"
				className="bg-green-500/15 text-green-700 dark:bg-green-500/20 dark:text-green-400"
			>
				ปกติ
			</Badge>
		)
	}

	return (
		<div className="flex flex-wrap gap-1">
			{deposit && (
				<Badge
					variant="destructive"
					className="flex w-fit items-center gap-1.5"
					title="ซ่อมบำรุงฝากเงิน"
				>
					<Icons.warning className="h-3.5 w-3.5" />
					<span className="text-xs">ฝาก</span>
				</Badge>
			)}
			{withdraw && (
				<Badge
					variant="destructive"
					className="flex w-fit items-center gap-1.5"
					title="ซ่อมบำรุงถอนเงิน"
				>
					<Icons.warning className="h-3.5 w-3.5" />
					<span className="text-xs">ถอน</span>
				</Badge>
			)}
		</div>
	)
}

export const createColumns = (
	constraints?: GetPaymentGatewayConstraintsResponse,
	channel?: PaymentChannelTab,
): ColumnDef<GetPaymentGateWayResponseData>[] => [
	// ลำดับ: ไม่แสดงในหน้าบัญชีถอน (ไม่สลับลำดับ)
	...(channel !== 'WITHDRAW_BANK'
		? [
				{
					header: 'ลำดับ',
					accessorKey: 'order',
					cell: ({
						row,
					}: {
						row: { original: GetPaymentGateWayResponseData }
					}) => (
						<div className="flex items-center justify-center">
							<span className="font-semibold text-muted-foreground">
								{row.original.order}
							</span>
						</div>
					),
				} as ColumnDef<GetPaymentGateWayResponseData>,
			]
		: []),
	{
		header: 'ข้อมูลบัญชี',
		accessorKey: 'displayName',
		cell: ({ row }) => {
			return (
				<div className="space-y-1">
					<p className="font-medium">{row.original.displayName}</p>
					<Badge variant="outline" className="font-medium">
						{row.original.bankProviderName}
					</Badge>
					{renderMaintenanceBadges(row.original)}
					{!row.original.isValidated && (
						<Badge
							variant="destructive"
							className="flex w-fit items-center gap-1.5"
							title="ข้อมูลไม่ปกติ"
						>
							<Icons.warning className="h-3.5 w-3.5" />
							<span className="text-xs">เชื่อมต่อไม่สำเร็จ</span>
						</Badge>
					)}
				</div>
			)
		},
	},
	{
		header: 'ยอดเงิน/วงเงิน',
		accessorKey: 'balance',
		cell: ({ row }) => {
			const isUnsupportedProvider =
				row.original.bankProviderName === 'GPAY' ||
				row.original.bankProviderName === 'TMN_WEBHOOK' ||
				row.original.bankProviderName === 'AUTOSSLIP'
			const balance = row.original.balance
			const min = row.original.minDepositAmount
			const max = row.original.maxDepositAmount
			const isCustomValue = min !== undefined && min !== null
			const isCustomMaxValue = max !== undefined && max !== null

			const fallbackMin = getProviderMinDeposit(
				row.original.bankProviderName,
				constraints,
			)
			const fallbackMax = constraints?.globalMaxDeposit
			const minValue = isCustomValue ? min : fallbackMin
			const maxValue = isCustomMaxValue ? max : fallbackMax

			return (
				<div className="space-y-1">
					{!row.original.isValidated ? (
						<p className="text-sm text-muted-foreground">ไม่สามารถตรวจสอบยอด</p>
					) : isUnsupportedProvider ? (
						<p className="text-sm text-muted-foreground">
							ไม่สามารถแสดงยอดเงิน
						</p>
					) : (
						<div className="flex items-center gap-1.5">
							<Wallet className="h-4 w-4 text-muted-foreground" />
							<span
								className={cn(
									'font-semibold',
									balance >= 0
										? 'text-green-600 dark:text-green-400'
										: 'text-red-600 dark:text-red-400',
								)}
							>
								฿
								{balance.toLocaleString('th-TH', {
									minimumFractionDigits: 2,
									maximumFractionDigits: 2,
								})}
							</span>
						</div>
					)}
					<p className="text-xs text-muted-foreground">
						ขั้นต่ำ:{' '}
						{minValue !== undefined
							? `฿${minValue.toLocaleString('th-TH')}`
							: '-'}
						{!isCustomValue && minValue !== undefined ? ' (default)' : ''}
					</p>
					<p className="text-xs text-muted-foreground">
						สูงสุด:{' '}
						{maxValue !== undefined
							? `฿${maxValue.toLocaleString('th-TH')}`
							: '-'}
						{!isCustomMaxValue && maxValue !== undefined ? ' (default)' : ''}
					</p>
				</div>
			)
		},
	},
	{
		header: 'ลิงก์',
		accessorKey: 'link',
		cell: ({ row }) =>
			providerNameToUrlMap[row.original.bankProviderName] ? (
				<Link
					to={providerNameToUrlMap[row.original.bankProviderName]}
					target="_blank"
				>
					<Button variant="outline" size="sm" className="gap-1.5">
						<ExternalLink className="h-3.5 w-3.5" />
						หลังบ้าน
					</Button>
				</Link>
			) : (
				<span className="text-sm text-muted-foreground">-</span>
			),
	},
	// สถานะ column: shown for all channels; in WITHDRAW_BANK tab, only rendered for
	// P2P accounts (isChannelP2P=true) — their isActive is locally managed so the
	// toggle must send channel='P2P' regardless of the active tab.
	...(channel !== 'WITHDRAW_BANK'
		? [
				{
					accessorKey: 'isActive',
					header: 'สถานะ',
					cell: ({
						row,
					}: {
						row: { original: GetPaymentGateWayResponseData }
					}) => {
						return <TogglePaymentGatewayStatus row={row} channel={channel} />
					},
				} as ColumnDef<GetPaymentGateWayResponseData>,
			]
		: [
				{
					accessorKey: 'isActive',
					header: 'สถานะ',
					cell: ({
						row,
					}: {
						row: { original: GetPaymentGateWayResponseData }
					}) => {
						if (!row.original.isChannelP2P) return null
						return <TogglePaymentGatewayStatus row={row} channel="P2P" />
					},
				} as ColumnDef<GetPaymentGateWayResponseData>,
			]),
	// Show withdrawal column only for WITHDRAW_BANK (shared QR+Transfer) and TRUE_MONEY tabs; hidden on QR and TRANSFER
	...(channel === 'WITHDRAW_BANK' || channel === 'TRUE_MONEY'
		? [
				{
					accessorKey: 'isWithdrawalAccount',
					header: 'บัญชีถอนเงิน',
					cell: ({
						row,
					}: {
						row: { original: GetPaymentGateWayResponseData }
					}) => {
						if (
							row.original.bankProviderName === 'SMS_AUTO' ||
							row.original.bankProviderName === 'AUTOSSLIP'
						) {
							return null
						}
						return (
							<TogglePaymentGatewayWithdrawalAccount
								row={row}
								channel={row.original.isChannelP2P ? 'P2P' : channel}
							/>
						)
					},
				} as ColumnDef<GetPaymentGateWayResponseData>,
			]
		: []),
	{
		id: 'actions',
		header: 'จัดการ',
		cell: ({ row }) => (
			<div className="flex flex-wrap items-center gap-1.5">
				<div className="flex items-center justify-center">
					<CreateDepositDialog
						paymentGatewayId={row.original.id}
						displayName={row.original.bankProviderName}
						channel={channel}
						enableUploadSlip={row.original.enableUploadSlip}
					/>
				</div>
				<div className="flex items-center justify-center">
					<CreateWithdrawalDialog
						paymentGatewayId={row.original.id}
						displayName={row.original.bankProviderName}
						channel={channel}
					/>
				</div>
				<Link to={`/operator/payment-gateway/${row.original.id}`}>
					<Button variant="ghost" size="icon" className="h-8 w-8">
						<Pencil className="h-4 w-4" />
					</Button>
				</Link>
				<DeletePaymentGateway id={row.original.id} />
			</div>
		),
	},
]

// Legacy export for backwards compatibility (without constraints)
export const columns = createColumns()

export const writeAccessorKeys = [
	'editOrder',
	'actions',
	'delete',
	'isActive',
	'isWithdrawalAccount',
	'deposit',
	'withdraw',
]
